import React from "react";
import { Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useTranslation } from "react-i18next";
import { startOfWeek, endOfWeek, eachDayOfInterval, format } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ResponsiveCharts from "../ChartsLayout/ResponsiveCharts.jsx";
import CustomGridItem from "../ChartsLayout/CustomGridItem.jsx";
import { HiddenTypography, BarChartEmpty } from "../ChartsLayout/EmptyCharts.jsx";
// import { calculateWeeksAndMonthsWithData } from "../../../utils/filterDataByDate.jsx";

export default function ConsumptionBarChart({
  consumptionData,
  consumptionInsights,
  selectedWeek,
}) {
  const weekStarts = startOfWeek(selectedWeek, { weekStartsOn: 1 });
  const weekEnds = endOfWeek(selectedWeek, { weekStartsOn: 1 });

  const weekDays = eachDayOfInterval({
    start: weekStarts,
    end: weekEnds,
  });

  const {t}= useTranslation("statistics");

  const getConsumptionData = (date) => {
    const dateFormatted = format(date, "yyyy-MM-dd");
    const dayConsumptionData = consumptionData.find(
      (data) => data.date === dateFormatted
    );
    return dayConsumptionData
      ? {
          water: dayConsumptionData.water ? dayConsumptionData.water : 0,
          coffee: dayConsumptionData.coffee ? dayConsumptionData.coffee : 0,
          refreshment: dayConsumptionData.refreshment
            ? dayConsumptionData.refreshment
            : 0,
          tea: dayConsumptionData.tea ? dayConsumptionData.tea : 0,
          alcohol: dayConsumptionData.alcohol ? dayConsumptionData.alcohol : 0,
        }
      : 0;
  };

  // const weeksMonthsWithData = calculateWeeksAndMonthsWithData(consumptionData);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ResponsiveCharts>
        {({ chartWidth }) => (
          <CustomGridItem>
            <div ref={consumptionInsights}>
              <Typography
                sx={{
                  margin: {
                    xs: "20px 0",
                    sm: "20px 0",
                    md: "0 0 20px 0",
                    lg: "20px 0 0 20px",
                    xl: "20px",
                  },
                  textAlign: "center",
                }}
                variant="h6"
              >
                {t("charts.consumption.title")}
              </Typography>
              {weekDays.some((day) => {
                const data = getConsumptionData(day);
                return data && Object.values(data).some((val) => val > 0);
              }) ? (
                <>
                  <HiddenTypography />
                  <BarChart
                    xAxis={[
                      {
                        scaleType: "band",
                        data: weekDays.map((day) => format(day, "EEE")),
                      },
                    ]}
                    series={[
                      {
                        data: weekDays.map(
                          (day) => getConsumptionData(day).water
                        ),
                        area: true,
                        label: t("series.waterMl"),
                      },
                      {
                        data: weekDays.map(
                          (day) => getConsumptionData(day).coffee
                        ),
                        area: true,
                        label: t("series.coffeeMl"),
                      },
                      {
                        data: weekDays.map(
                          (day) => getConsumptionData(day).refreshment
                        ),
                        area: true,
                        label: t("series.refreshmentMl"),
                      },
                      {
                        data: weekDays.map(
                          (day) => getConsumptionData(day).tea
                        ),
                        area: true,
                        label: t("series.teaMl"),
                      },
                      {
                        data: weekDays.map(
                          (day) => getConsumptionData(day).alcohol
                        ),
                        area: true,
                        label: t("series.alcoholMl"),
                      },
                    ]}
                    width={chartWidth}
                    height={300}
                    borderRadius={10}
                    slotProps={{
                      legend: {
                        direction: "row",
                        position: { vertical: "top", horizontal: "right" },
                        padding: -5,
                        itemMarkWidth: 20,
                        itemMarkHeight: 3,
                        markGap: 5,
                        itemGap: 5,
                      },
                    }}
                  />
                </>
              ) : (
                <BarChartEmpty weekDays={weekDays} chartWidth={chartWidth} />
              )}
            </div>
          </CustomGridItem>
        )}
      </ResponsiveCharts>
    </LocalizationProvider>
  );
}
